import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

export type CustomFieldEntity = "contacts" | "companies" | "deals";
export type CustomFieldType = "text" | "number" | "date" | "boolean" | "select";

export interface CustomFieldDefinition {
  id: string;
  entity: CustomFieldEntity;
  key: string;
  label: string;
  field_type: CustomFieldType;
  options: string[];
  position: number;
}

export const CUSTOM_FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes / No" },
  { value: "select", label: "Select" },
];

export const customFieldsQueryKey = (entity?: CustomFieldEntity) =>
  ["gravun", "custom-fields", getActiveWorkspaceId(), entity].filter(
    (part) => part !== undefined,
  );

/** Workspace custom-field definitions for one entity (RLS + scope applied). */
export const useCustomFieldDefinitions = (entity: CustomFieldEntity) => {
  const workspaceId = getActiveWorkspaceId();
  return useQuery({
    queryKey: customFieldsQueryKey(entity),
    staleTime: 60_000,
    queryFn: async (): Promise<CustomFieldDefinition[]> => {
      let query = getSupabaseClient()
        .from("custom_field_definitions")
        .select("id, entity, key, label, field_type, options, position")
        .eq("entity", entity)
        .order("position")
        .order("created_at");
      if (workspaceId) query = query.eq("workspace_id", workspaceId);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...row,
        options: Array.isArray(row.options) ? row.options : [],
      })) as CustomFieldDefinition[];
    },
  });
};

/** Derive a stable snake_case key from a human label. */
export const labelToKey = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
